import { createContext, useContext } from 'react';
import type { AdminVideo, VideoPurpose } from '@/api/types';

export type UploadPhase = 'creating' | 'uploading' | 'paused' | 'uploaded' | 'error';

export interface UploadItem {
  /** Local key; equals the video id once created. */
  key: string;
  videoId: string | null;
  title: string;
  fileName: string;
  size: number;
  purpose: VideoPurpose;
  phase: UploadPhase;
  bytesSent: number;
  error: string | null;
}

export interface StartOptions {
  file: File;
  title: string;
  purpose: VideoPurpose;
  /** Called once the video row exists (e.g. to attach it to an episode straight away). */
  onCreated?: (video: AdminVideo) => void | Promise<void>;
}

interface UploadsContextValue {
  uploads: UploadItem[];
  start: (opts: StartOptions) => Promise<AdminVideo | null>;
  pause: (key: string) => void;
  resume: (key: string) => Promise<void>;
  dismiss: (key: string) => void;
}

export const UploadsContext = createContext<UploadsContextValue | null>(null);

export function useUploads(): UploadsContextValue {
  const ctx = useContext(UploadsContext);
  if (!ctx) throw new Error('useUploads outside UploadsProvider');
  return ctx;
}

export function useUploadFor(videoId: string | null | undefined): UploadItem | undefined {
  const { uploads } = useUploads();
  return videoId ? uploads.find((u) => u.videoId === videoId) : undefined;
}
